// src/components/communication/ChatMessages.jsx
import React from 'react';
import Button from '../common/Button';

const ChatMessages = ({
  thread,
  messages,
  loading,
  messageInput,
  setMessageInput,
  onSendMessage,
  messagesEndRef,
  vendorId
}) => {
  // Format date
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    
    // If already a string, return as is
    if (typeof timestamp === 'string') return timestamp;
    
    // Convert Firestore Timestamp to JavaScript Date
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  // Format date
  const formatMessageDate = (timestamp) => {
    if (!timestamp) return '';
    
    // If already a string, return as is
    if (typeof timestamp === 'string') return timestamp;
    
    // Convert Firestore Timestamp to JavaScript Date
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  // Group messages by date
  const groupMessagesByDate = (msgs) => {
    const groups = {};
    
    msgs.forEach(msg => {
      const timestamp = msg.timestamp?.toDate ? msg.timestamp.toDate() : new Date(msg.timestamp);
      const date = timestamp.toLocaleDateString();
      
      if (!groups[date]) {
        groups[date] = [];
      }
      
      groups[date].push(msg);
    });
    
    return Object.entries(groups).map(([date, messages]) => ({
      date,
      messages
    }));
  };
  
  const messageGroups = groupMessagesByDate(messages);
  
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            {thread.customerAvatar ? (
              <img 
                className="h-10 w-10 rounded-full" 
                src={thread.customerAvatar} 
                alt={thread.customerName || 'Customer'} 
              />
            ) : (
              <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                <span className="text-orange-600 font-medium text-sm">
                  {thread.customerName?.charAt(0).toUpperCase() || 'C'}
                </span>
              </div>
            )}
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-gray-900">
              {thread.customerName || 'Unknown Customer'}
            </p>
            {thread.customerEmail && (
              <p className="text-xs text-gray-500">
                {thread.customerEmail}
              </p>
            )}
          </div>
        </div>
      </div>
      
      {/* Messages */}
      <div className="flex-1 p-4 overflow-y-auto bg-gray-50">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="mt-2 text-sm text-gray-500">No messages yet</p>
            <p className="text-sm text-gray-500">Send a message to start the conversation</p>
          </div>
        ) : (
          <div className="space-y-6">
            {messageGroups.map((group, groupIndex) => (
              <div key={groupIndex} className="space-y-2">
                <div className="flex justify-center">
                  <div className="bg-gray-200 rounded-full px-3 py-1">
                    <p className="text-xs text-gray-600">{formatMessageDate(new Date(group.date))}</p>
                  </div>
                </div>
                
                {group.messages.map((message, messageIndex) => {
                  const isVendor = message.senderType === 'vendor' || message.senderId === vendorId;
                  
                  return (
                    <div 
                      key={message.id || messageIndex} 
                      className={`flex ${isVendor ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-xs lg:max-w-md ${message.pending ? 'opacity-70' : ''}`}>
                        <div className={`
                          px-4 py-2 rounded-lg shadow-sm 
                          ${isVendor 
                            ? 'bg-orange-600 text-white' 
                            : 'bg-white text-gray-900 border border-gray-200'}
                        `}>
                          {message.isBroadcast && isVendor && (
                            <div className="text-xs text-orange-200 mb-1">Broadcast message</div>
                          )}
                          
                          <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                        </div>
                        <div className={`mt-1 text-xs flex ${isVendor ? 'justify-end' : 'justify-start'}`}>
                          <span className={isVendor ? 'text-gray-500' : 'text-gray-500'}>
                            {formatMessageTime(message.timestamp)}
                            {message.pending && (
                              <span className="ml-1">
                                <svg className="inline-block animate-spin h-3 w-3 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                                </svg>
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
            
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>
      
      {/* Message Input */}
      <div className="border-t border-gray-200 bg-white p-4">
        <form onSubmit={onSendMessage} className="flex space-x-2">
          <div className="flex-1">
            <input
              type="text"
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              placeholder="Type a message..."
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
            />
          </div>
          <Button
            type="submit"
            variant="primary"
            disabled={!messageInput.trim()}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
            </svg>
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ChatMessages;